import { Box, CircularProgress, Tooltip } from "@mui/material";
import { useResourceStatus } from "../hooks";
import { isActiveStatus } from "../types";

interface ResourceStatusIndicatorProps {
  type: string;
  id: string | number;
}

const DOT_COLOR: Record<string, string> = {
  completed: "success.main",
  error: "error.main",
  cancelled: "warning.main",
};

/**
 * Tiny live-status marker for a row in a list: spinner while active, a
 * colored dot once finished (until the user views the resource), nothing
 * when idle.
 */
const ResourceStatusIndicator = ({
  type,
  id,
}: ResourceStatusIndicatorProps) => {
  const status = useResourceStatus(type, id);
  if (status === null) return null;

  if (isActiveStatus(status)) {
    return (
      <Tooltip title="Working…">
        <CircularProgress
          size={14}
          thickness={5}
          data-testid={`resource-status-${type}-${id}`}
          data-status={status}
          aria-label="Working"
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={status.charAt(0).toUpperCase() + status.slice(1)}>
      <Box
        component="span"
        data-testid={`resource-status-${type}-${id}`}
        data-status={status}
        aria-label={status}
        sx={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: DOT_COLOR[status] ?? "info.main",
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );
};

export default ResourceStatusIndicator;
